import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TEMPLATES } from "../data/templates";
import { request } from "../services/api";

const REFRESH_EVERY = 10 * 60 * 1000;

// The template catalogue, as set from the admin dashboard through the API
// (GET /api/catalog): for each template, its price, whether hosts can pick
// it, and whether it is featured. Until the API answers — or if it can't be
// reached — the prices in data/prices.json are used and every template is
// offered. The API charges its own prices whatever the app shows.
const CatalogContext = createContext(null);

export function CatalogProvider({ children }) {
  const [catalog, setCatalog] = useState({ templates: {}, loaded: false });

  useEffect(() => {
    let alive = true;
    const load = () =>
      request("GET", "/catalog")
        .then((remote) => alive && setCatalog({ templates: remote?.templates || {}, loaded: true }))
        .catch(() => alive && setCatalog((current) => ({ ...current, loaded: true })));
    load();
    const timer = setInterval(load, REFRESH_EVERY);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  const entry = useCallback((id) => catalog.templates[id], [catalog]);

  const priceOf = useCallback((template) => entry(template.id)?.price ?? template.price, [entry]);
  const isOffered = useCallback((template) => entry(template.id)?.enabled !== false, [entry]);
  const isFeatured = useCallback((template) => Boolean(entry(template.id)?.featured), [entry]);

  // The templates hosts can pick for an event type (all types when empty):
  // featured first, then new ones, then exclusive designs. `keep` stays
  // listed even if it was withdrawn, so a draft already using it still shows
  // its choice.
  const templatesForType = useCallback(
    (typeId, keep) =>
      TEMPLATES.filter((template) => (!typeId || template.types.includes(typeId)) && (isOffered(template) || template.id === keep))
        .map((template, index) => ({ template, index }))
        .sort(
          (a, b) =>
            Number(isFeatured(b.template)) - Number(isFeatured(a.template)) ||
            Number(Boolean(b.template.isNew)) - Number(Boolean(a.template.isNew)) ||
            Number(Boolean(b.template.exclusive)) - Number(Boolean(a.template.exclusive)) ||
            a.index - b.index
        )
        .map(({ template }) => template),
    [isOffered, isFeatured]
  );

  const value = useMemo(
    () => ({ loaded: catalog.loaded, priceOf, isOffered, isFeatured, templatesForType }),
    [catalog.loaded, priceOf, isOffered, isFeatured, templatesForType]
  );

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

// Without a provider (e.g. a component rendered alone in a test), the
// defaults apply.
const fallback = {
  loaded: true,
  priceOf: (template) => template.price,
  isOffered: () => true,
  isFeatured: () => false,
  templatesForType: (typeId) => TEMPLATES.filter((template) => !typeId || template.types.includes(typeId)),
};

export const useCatalog = () => useContext(CatalogContext) || fallback;
